import type { BillingDocument, BillingState } from "../types";
import { money } from "./utils";

const esc = (v: unknown) =>
  String(v ?? "").replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c)
  );

export function generateDocumentHtml(doc: BillingDocument, state: BillingState): string {
  const s = state.settings;
  const isInvoice = doc.type === "invoice";
  const title = isInvoice ? "TAX INVOICE" : "QUOTATION";

  const rows = doc.items
    .map(
      (item, index) => `
    <tr>
      <td style="text-align: center; width: 40px;">${index + 1}</td>
      <td>
        <div style="font-weight: 600; color: #0d263e; font-size: 13px;">${esc(item.desc || "Item " + (index + 1))}</div>
      </td>
      <td style="text-align: center; width: 75px;">${esc(item.hsn || "998361")}</td>
      <td style="text-align: center; width: 55px;">${item.qty}</td>
      <td style="text-align: center; width: 65px;">${esc(item.unit || "Month")}</td>
      <td style="text-align: right; width: 95px;">${money(item.rate)}</td>
      <td style="text-align: right; width: 70px;">${item.disc ? item.disc + "%" : "—"}</td>
      <td style="text-align: right; width: 110px; font-weight: 700; color: #0d263e;">${money(item.amount)}</td>
    </tr>
  `
    )
    .join("");

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>${esc(doc.number)} - ${esc(s.name)}</title>
  <style>
    @page { size: A4 portrait; margin: 12mm 15mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      color: #1a2a3a;
      background: #ffffff;
      font-size: 12px;
      line-height: 1.45;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .invoice-box {
      max-width: 800px;
      margin: auto;
      padding: 0;
      position: relative;
    }
    .header-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 3px solid #0d263e;
      padding-bottom: 16px;
      margin-bottom: 22px;
    }
    .brand {
      display: flex;
      align-items: center;
      gap: 16px;
    }
    .brand img {
      width: 58px;
      height: 58px;
      object-fit: contain;
      border-radius: 8px;
    }
    .brand h1 {
      margin: 0;
      font-size: 24px;
      font-weight: 800;
      color: #0d263e;
      letter-spacing: -0.5px;
    }
    .brand h1 span { color: #d4ac42; }
    .brand .tagline {
      margin: 3px 0 0;
      font-size: 11px;
      color: #718292;
      text-transform: uppercase;
      letter-spacing: 0.6px;
    }
    .doc-badge-section {
      text-align: right;
    }
    .doc-type {
      font-size: 26px;
      font-weight: 800;
      color: #0d263e;
      letter-spacing: 1px;
      margin: 0;
      text-transform: uppercase;
    }
    .doc-num {
      font-size: 14px;
      font-weight: 700;
      color: #d4ac42;
      margin-top: 3px;
    }
    .grid-meta {
      display: grid;
      grid-template-columns: 1.2fr 0.8fr;
      gap: 28px;
      margin-bottom: 24px;
      padding: 16px;
      background: #f8fafc;
      border-radius: 8px;
      border: 1px solid #eef2f6;
    }
    .label {
      font-size: 10px;
      text-transform: uppercase;
      color: #64748b;
      letter-spacing: 0.7px;
      margin-bottom: 4px;
      font-weight: 700;
    }
    .client-name {
      font-size: 16px;
      font-weight: 700;
      color: #0d263e;
      margin-bottom: 4px;
    }
    .client-details {
      color: #475569;
      font-size: 12px;
      line-height: 1.5;
    }
    .meta-table {
      width: 100%;
      border-collapse: collapse;
    }
    .meta-table tr td {
      padding: 4px 0;
      font-size: 12px;
      border-bottom: 1px dashed #e2e8f0;
    }
    .meta-table tr:last-child td {
      border-bottom: none;
    }
    .meta-table .m-label {
      color: #64748b;
      font-weight: 500;
    }
    .meta-table .m-val {
      text-align: right;
      font-weight: 700;
      color: #0d263e;
    }
    .items-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 24px;
    }
    .items-table th {
      background: #0d263e;
      color: #ffffff;
      padding: 10px 8px;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.6px;
      font-weight: 700;
    }
    .items-table td {
      padding: 10px 8px;
      border-bottom: 1px solid #e2e8f0;
      font-size: 12px;
    }
    .items-table tbody tr:nth-child(even) {
      background: #fafbfd;
    }
    .summary-grid {
      display: grid;
      grid-template-columns: 1.1fr 0.9fr;
      gap: 30px;
      margin-bottom: 28px;
    }
    .bank-box {
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 14px;
      background: #ffffff;
    }
    .bank-box h4 {
      margin: 0 0 8px;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.7px;
      color: #0d263e;
      border-bottom: 1px solid #eef2f6;
      padding-bottom: 5px;
    }
    .bank-box p {
      margin: 3px 0;
      font-size: 11px;
      color: #334155;
    }
    .bank-box strong { color: #0d263e; }
    .totals-box {
      margin-left: auto;
      width: 100%;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      overflow: hidden;
      background: #ffffff;
    }
    .totals-row {
      display: flex;
      justify-content: space-between;
      padding: 7px 14px;
      font-size: 12px;
      color: #475569;
      border-bottom: 1px solid #f1f5f9;
    }
    .totals-row b {
      color: #0d263e;
    }
    .totals-row.grand-total {
      background: #0d263e;
      color: #ffffff;
      padding: 12px 14px;
      font-size: 15px;
      font-weight: 800;
      border-bottom: none;
    }
    .totals-row.grand-total b {
      color: #ffffff;
    }
    .notes-section {
      border-top: 1px solid #e2e8f0;
      padding-top: 14px;
      margin-bottom: 24px;
    }
    .notes-section p {
      margin: 4px 0 0;
      color: #64748b;
      font-size: 11px;
      white-space: pre-line;
    }
    .footer-bar {
      border-top: 2px solid #0d263e;
      padding-top: 12px;
      display: flex;
      justify-content: space-between;
      font-size: 10px;
      color: #64748b;
    }
    .status-badge {
      display: inline-block;
      padding: 3px 8px;
      border-radius: 4px;
      font-weight: 700;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .status-paid { background: #dcfce7; color: #15803d; }
    .status-sent { background: #dbeafe; color: #1d4ed8; }
    .status-draft { background: #f1f5f9; color: #475569; }
    .status-accepted { background: #ecfdf5; color: #047857; }
    @media print {
      body { background: transparent; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="invoice-box">
    <div class="header-bar">
      <div class="brand">
        <img src="${window.location.origin}/logo.png" alt="Logo" onerror="this.style.display='none'" />
        <div>
          <h1>Revival <span>Softech</span></h1>
          <div class="tagline">${esc(s.tagline || "Innovative Technology Solutions")}</div>
          <div style="font-size: 11px; color: #475569; margin-top: 2px;">${esc(s.legalName || "Revival Business Solution")}</div>
        </div>
      </div>
      <div class="doc-badge-section">
        <div class="doc-type">${title}</div>
        <div class="doc-num">${esc(doc.number)}</div>
        <div style="margin-top: 4px;">
          <span class="status-badge status-${esc(doc.status)}">${esc(doc.status)}</span>
        </div>
      </div>
    </div>

    <div class="grid-meta">
      <div>
        <div class="label">Billed To</div>
        <div class="client-name">${esc(doc.company || "Client")}</div>
        <div class="client-details">
          ${doc.address ? `<div>${esc(doc.address)}</div>` : ""}
          ${doc.email ? `<div><strong>Email:</strong> ${esc(doc.email)}</div>` : ""}
          ${doc.phone ? `<div><strong>Phone:</strong> ${esc(doc.phone)}</div>` : ""}
          ${doc.clientGstin ? `<div><strong>GSTIN:</strong> ${esc(doc.clientGstin)}</div>` : ""}
        </div>
      </div>
      <div>
        <table class="meta-table">
          <tr>
            <td class="m-label">${isInvoice ? "Invoice Date" : "Quotation Date"}:</td>
            <td class="m-val">${esc(doc.date)}</td>
          </tr>
          <tr>
            <td class="m-label">${isInvoice ? "Due Date" : "Valid Until"}:</td>
            <td class="m-val">${esc(doc.due)}</td>
          </tr>
          <tr>
            <td class="m-label">Place of Supply:</td>
            <td class="m-val">${esc(doc.pos || "Karnataka (29)")}</td>
          </tr>
          ${doc.po ? `<tr><td class="m-label">P.O. Number:</td><td class="m-val">${esc(doc.po)}</td></tr>` : ""}
          <tr>
            <td class="m-label">Payment Mode:</td>
            <td class="m-val">${esc(doc.paymentMethod || "Bank Transfer / NEFT")}</td>
          </tr>
        </table>
      </div>
    </div>

    <table class="items-table">
      <thead>
        <tr>
          <th>#</th>
          <th style="text-align: left;">Item & Description</th>
          <th>HSN/SAC</th>
          <th>Qty</th>
          <th>Unit</th>
          <th style="text-align: right;">Rate (₹)</th>
          <th style="text-align: right;">Disc %</th>
          <th style="text-align: right;">Amount (₹)</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
    </table>

    <div class="summary-grid">
      <div class="bank-box">
        <h4>Bank Transfer Details</h4>
        <p><strong>Bank Name:</strong> ${esc(s.bank || "HDFC Bank")}</p>
        <p><strong>Account Name:</strong> ${esc(s.legalName || "Revival Business Solution")}</p>
        <p><strong>Account Number:</strong> ${esc(s.acc || "50200012345678")}</p>
        <p><strong>IFSC Code:</strong> ${esc(s.ifsc || "HDFC0001234")}</p>
        <p><strong>Account Type:</strong> ${esc(s.acctype || "Current")}</p>
      </div>

      <div class="totals-box">
        <div class="totals-row">
          <span>Sub Total</span>
          <b>${money(doc.subtotal)}</b>
        </div>
        ${doc.discTotal > 0 ? `
          <div class="totals-row" style="color: #b91c1c;">
            <span>Discount</span>
            <b>-${money(doc.discTotal)}</b>
          </div>
        ` : ""}
        <div class="totals-row">
          <span>GST (${doc.gstRate}%)</span>
          <b>${money(doc.gstAmt)}</b>
        </div>
        <div class="totals-row grand-total">
          <span>Total Payable</span>
          <b>${money(doc.total)}</b>
        </div>
      </div>
    </div>

    <div class="notes-section">
      <div class="label">Terms & Notes</div>
      <p>${esc(doc.notes || s.notes || "Thank you for doing business with Revival Softech.")}</p>
    </div>

    <div class="footer-bar">
      <div>${esc(s.address || "HSR Layout, Bengaluru, Karnataka, India")}</div>
      <div>${esc(s.email)} · ${esc(s.website)} · GSTIN: ${esc(s.gstin)}</div>
    </div>
  </div>
</body>
</html>`;
}

export function printDocument(doc: BillingDocument, state: BillingState) {
  const html = generateDocumentHtml(doc, state);
  const printWindow = window.open("", "_blank", "width=900,height=1000");
  if (!printWindow) {
    alert("Please allow popups to print/download your document.");
    return;
  }
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 400);
}
